import { MailOutlined, StarOutlined } from '@ant-design/icons';
import {
  EventNotificationFrequencies,
  EventNotificationFrequencyWeekly,
  Product,
  WaterbodyDetail,
} from '@omniafishing/core';
import { Checkbox, Input, Modal } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';
import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useLocalStorage } from '../../../../hooks/use_local_storage';
import { useQueryString } from '../../../../hooks/use_query_string';
import { useRudderstack } from '../../../../hooks/use_rudderstack';
import { useUserWaterbodies } from '../../../../hooks/use_user_waterbodies';
import { apiV1 } from '../../../../lib/api';
import { StorageKeys } from '../../../../lib/local_storage';
import { WebAnalytics } from '../../../../lib/web_analytics';
import { hasAccessToken } from '../../../../redux/auth';
import { getIp } from '../../../../redux/geographic_location';
import { getIsWebview } from '../../../../redux/window';
import { RoutePaths } from '../../../../routes';
import { CURRENT_YEAR } from '../../../footer/subfooter';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import fallbackBaitImage from '../../images/modal_images/fallback_bait.jpg';
import modalImage from '../../images/modal_images/modal_graphic.png';
import styles from './waterbody_detail_follow_modal.less';

export interface WaterbodyDetailFollowModalProps {
  waterbody: WaterbodyDetail;
  open: boolean;
  onModalChange: (open: boolean) => void;
  productToShow: Product;
  onSuccess?: () => void;
  isTimedPopup?: boolean;
  selectedSpeciesName: string;
}

export interface FollowLakeFormValues {
  email: string;
  frequency: EventNotificationFrequencies;
  newsletter_opt_in: boolean;
}

const LAKE_PAGE_FOLLOW_MODAL = 'Lake Page Follow Modal';

const SPECIES_IMAGES = {
  largemouth_bass: 'largemouth_bass',
  smallmouth_bass: 'smallmouth_bass',
  spotted_bass: 'spotted_bass',
  walleye: 'walleye',
  crappie: 'crappie',
} as const;

export const WaterbodyDetailFollowModal = (props: WaterbodyDetailFollowModalProps) => {
  const {
    waterbody,
    open,
    onModalChange,
    onSuccess,
    isTimedPopup,
    productToShow,
    selectedSpeciesName,
  } = props;
  const { primary_name } = waterbody;
  const { anonymousId } = useRudderstack();
  const ipAddress = useSelector(getIp);
  const isWebview = useSelector(getIsWebview);
  const isLoggedIn = useSelector(hasAccessToken);
  const { replaceQueryString } = useQueryString();
  const formRef = useRef<FormInstance>();
  const { favoriteWaterbody } = useUserWaterbodies();

  const speciesImageName =
    SPECIES_IMAGES[selectedSpeciesName as keyof typeof SPECIES_IMAGES] || 'fallback';

  const formFields = {
    email: 'email',
    frequency: 'frequency',
    newsletter_opt_in: 'newsletter_opt_in',
  };

  const [waterbodySubscribeModalClosed, setWaterbodySubscribeModalClosed] = useLocalStorage(
    StorageKeys.waterbodySubscribeModalClosed,
    false
  );

  useEffect(() => {
    setTimeout(() => {
      if (!waterbodySubscribeModalClosed && !isLoggedIn && isTimedPopup && !isWebview) {
        onModalChange(true);
      }
    }, 5000);
  }, [isTimedPopup, isLoggedIn]);

  const onOk = () => {
    const form = formRef.current;

    form
      .validateFields()
      .then((values: FollowLakeFormValues) => {
        const { email, newsletter_opt_in } = values;
        const event_notifications = {
          new_fishing_report: true,
          new_article: true,
          frequency: EventNotificationFrequencyWeekly as EventNotificationFrequencies,
        };

        if (newsletter_opt_in) {
          apiV1.newsletterSubscribe({
            email: values.email,
            anonymous_id: anonymousId,
            user_agent: window.navigator.userAgent,
            ip_address: ipAddress,
            location: LAKE_PAGE_FOLLOW_MODAL,
          });
        }

        favoriteWaterbody.mutate(
          {
            event_notifications,
            email,
            url_slug: waterbody.url_slug,
          },
          {
            onSuccess: () => {
              onSuccess?.();
              replaceQueryString({ following: true, email });
              WebAnalytics.followUpEnabled(email);
            },
            onSettled: (data, error, variables, context) => {
              onModalChange(false);
              if (error) {
                console.error(error);
              }
            },
          }
        );
        setWaterbodySubscribeModalClosed(true);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  return (
    <Modal
      wrapClassName={'notificationModalContainer'}
      open={open}
      title={
        <div className={styles.modalTitle}>
          <strong>Weekly {primary_name} updates</strong> <br />
          <span>
            Get the Latest {CURRENT_YEAR} {primary_name} Fishing Reports by Email.
          </span>
        </div>
      }
      onOk={onOk}
      onCancel={() => {
        onModalChange(false);
        setWaterbodySubscribeModalClosed(true);
      }}
      okText={'Notify Me'}
      destroyOnClose
      okButtonProps={{
        loading: favoriteWaterbody.isPending,
      }}
      cancelButtonProps={{
        disabled: favoriteWaterbody.isPending,
      }}
      footer={null}
    >
      <div className={styles.heroImage}>
        <span className={classNames(styles[speciesImageName], styles.speciesImage)}></span>
        <div className={styles.productImageContainer}>
          <img
            className={styles.infoGraphic}
            src={modalImage}
            alt="fishing report and hot bait image"
          />
          <img
            className={styles.productImage}
            src={productToShow?.img_url || fallbackBaitImage}
            alt={`hot bait image`}
          />
        </div>
      </div>
      <Form
        ref={formRef}
        initialValues={{
          [formFields.newsletter_opt_in]: false,
          [formFields.frequency]: EventNotificationFrequencyWeekly,
        }}
        layout="vertical"
      >
        <Form.Item
          label={'Email'}
          name={formFields.email}
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input
            type="email"
            prefix={<MailOutlined className="site-form-item-icon" />}
            placeholder="Email Address"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name={formFields.newsletter_opt_in} valuePropName="checked">
          <Checkbox>
            Also sign me up for tips and deals from the Omnia newsletter. <br />
            <Link to={RoutePaths.NEWSLETTER} target="_blank">
              Learn more.
            </Link>
          </Checkbox>
        </Form.Item>
      </Form>
      <div className={styles.modalFooter}>
        <OmniaButton
          kind="tertiary"
          size="lg"
          fontSize={14}
          isDisabled={favoriteWaterbody.isPending}
          onPress={() => {
            onModalChange(false);
            setWaterbodySubscribeModalClosed(true);
          }}
        >
          Cancel
        </OmniaButton>
        <OmniaButton
          kind="primary"
          size="lg"
          fontSize={14}
          onPress={onOk}
          loading={favoriteWaterbody.isPending}
        >
          <StarOutlined style={{ fontSize: '17px', width: '17px' }} /> Follow
        </OmniaButton>
      </div>
    </Modal>
  );
};
